import { makeStyles, shorthands, tokens } from "@fluentui/react-components";
import { useEffect, useRef } from "react";
import { useCurrentSystemInfoBanner } from "../../hooks/useSystemInfoBanner";
import { SkanskaColorTokens } from "../../style/colors";

const systemBannerHeightCssVariable = "--vsaa-system-banner-height";

const useClasses = makeStyles({
  wrapper: {
    width: "100%",
    position: "sticky",
    top: "var(--vsaa-header-height, 0px)",
    zIndex: 999,
    backgroundColor: SkanskaColorTokens.color_tertiary_yellow,
    color: SkanskaColorTokens.color_secondary_black,
    ...shorthands.borderTop("1px", "solid", "rgba(0, 0, 0, 0.10)"),
    ...shorthands.borderBottom("1px", "solid", "rgba(0, 0, 0, 0.14)"),
  },
  content: {
    paddingTop: tokens.spacingVerticalS,
    paddingRight: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalL,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase400,
    textAlign: "center",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },
});

function SystemInfoBanner() {
  const classes = useClasses();
  const { data: banner } = useCurrentSystemInfoBanner();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = wrapperRef.current;

    if (!element) {
      document.documentElement.style.setProperty(
        systemBannerHeightCssVariable,
        "0px"
      );
      return;
    }

    const updateHeight = () => {
      document.documentElement.style.setProperty(
        systemBannerHeightCssVariable,
        `${element.offsetHeight}px`
      );
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      document.documentElement.style.setProperty(
        systemBannerHeightCssVariable,
        "0px"
      );
    };
  }, [banner?.message]);

  if (!banner?.message) {
    return null;
  }

  return (
    <div
      ref={wrapperRef}
      className={classes.wrapper}
      data-print-hide="true"
      id="vsaa-system-info-banner"
    >
      <div className={classes.content}>{banner.message}</div>
    </div>
  );
}

export default SystemInfoBanner;
